import { type FC, type PropsWithChildren, useEffect } from "react";
import { Box, CircularProgress, Zoom } from "@mui/material";
import {
  SignIn,
  NotPartnerScreen,
  NoOrganizationScreen,
} from "@/partner/features/auth/";
import { useCurrentUserQuery } from "@/partner/entities/auth";
import { AuthProvider as AuthClientProvider } from "@/partner/entities/auth";
import { ErrorMessage } from "@/shared/components";
import type { UserType } from "@/types/graphql/partner.generated";
import i18n from "./i18n";

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data, loading, error } = useCurrentUserQuery();
  const currentUser = data?.currentUser;

  // Сохранённый на юзере язык — единственный источник персистентности (в
  // кабинете нет i18next-browser-languagedetector/localStorage, в отличие
  // от референса). Применяем один раз при первой загрузке юзера, иначе
  // UI после reload/логина снова показывает дефолт из i18n.ts ("en"),
  // даже если пользователь ранее переключился на "ru".
  useEffect(() => {
    if (currentUser?.language && currentUser.language !== i18n.language) {
      i18n.changeLanguage(currentUser.language);
    }
  }, [currentUser?.language]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Zoom in>
          <CircularProgress size={100} />
        </Zoom>
      </Box>
    );
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  if (!currentUser) return <SignIn />;

  const isAdminUser = currentUser.isAdmin || currentUser.isSuperAdmin;

  if (!currentUser.isPartner && !isAdminUser) {
    return <NotPartnerScreen />;
  }

  if (!currentUser.currentOrganization) {
    return <NoOrganizationScreen />;
  }

  return (
    <AuthClientProvider user={currentUser as UserType}>
      {children}
    </AuthClientProvider>
  );
};
